import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { CookieBanner } from "@/components/CookieBanner";
import { AuthProvider } from "@/context/AuthContext";
import { AuthCallback } from "@/components/AuthCallback";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppLayout } from "@/components/AppLayout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import GrantSearch from "@/pages/GrantSearch";
import GrantDetail from "@/pages/GrantDetail";
import Proposals from "@/pages/Proposals";
import ProposalEditor from "@/pages/ProposalEditor";
import Licensing from "@/pages/Licensing";
import LicenseGuide from "@/pages/LicenseGuide";
import Reviews from "@/pages/Reviews";
import Admin from "@/pages/Admin";
import Billing from "@/pages/Billing";
import Company from "@/pages/Company";
import Documents from "@/pages/Documents";
import CalendarPage from "@/pages/CalendarPage";
import Awards from "@/pages/Awards";
import AwardDetail from "@/pages/AwardDetail";
import Invite from "@/pages/Invite";
import MasterAdmin from "@/pages/MasterAdmin";
import ContentLibrary from "@/pages/ContentLibrary";
import BlueprintPage from "@/pages/Blueprint";
import Budget from "@/pages/Budget";
import Portfolio from "@/pages/Portfolio";
import Capital from "@/pages/Capital";
import LeaseLoi from "@/pages/LeaseLoi";
import FundingReadiness from "@/pages/FundingReadiness";
import TopGrantIndustries from "@/pages/TopGrantIndustries";
import IndustryPage from "@/pages/IndustryPage";
import StateIndustryPage from "@/pages/StateIndustryPage";
import FundingReadyPublic from "@/pages/FundingReadyPublic";
import WorkshopRegister from "@/pages/WorkshopRegister";
import WorkshopCancel from "@/pages/WorkshopCancel";
import WorkshopFeedback from "@/pages/WorkshopFeedback";
import LibraryPage from "@/pages/LibraryPage";
import LibraryStats from "@/pages/LibraryStats";
import Alerts from "@/pages/Alerts";
import Pros from "@/pages/Pros";
import Verify from "@/pages/Verify";
import ProPortal from "@/pages/ProPortal";
import Inquiries from "@/pages/Inquiries";
import Playbook from "@/pages/Playbook";
import SavedSearchAction from "@/pages/SavedSearchAction";
import BadgeWall from "@/pages/BadgeWall";
import Legal from "@/pages/Legal";
import Partners from "@/pages/Partners";
import Pricing from "@/pages/Pricing";
import About from "@/pages/About";
import Features from "@/pages/Features";
import Compare from "@/pages/Compare";
import FounderCohort from "@/pages/FounderCohort";
import CohortCheckin from "@/pages/CohortCheckin";
import CaseStudyApprove from "@/pages/CaseStudyApprove";
import Customers from "@/pages/Customers";
import { TourProvider } from "@/components/tour/TourProvider";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import Welcome from "@/pages/Welcome";

function AppRouter() {
  const location = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/thank-you" element={<PaymentSuccess />} />
      <Route path="/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/dashboard" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="grants" element={<GrantSearch />} />
        <Route path="grants/:id" element={<GrantDetail />} />
        <Route path="proposals" element={<Proposals />} />
        <Route path="proposals/:id" element={<ProposalEditor />} />
        <Route path="licensing" element={<Licensing />} />
        <Route path="licensing/:id" element={<LicenseGuide />} />
        <Route path="reviews" element={<Reviews />} />
        <Route path="billing" element={<Billing />} />
        <Route path="admin" element={<Admin />} />
        <Route path="master-admin" element={<MasterAdmin />} />
        <Route path="content-library" element={<ContentLibrary />} />
        <Route path="blueprint" element={<BlueprintPage />} />
        <Route path="blueprint/:id" element={<BlueprintPage />} />
        <Route path="playbook" element={<Playbook />} />
        <Route path="budget" element={<Budget />} />
        <Route path="portfolio" element={<Portfolio />} />
        <Route path="capital" element={<Capital />} />
        <Route path="lease-loi" element={<LeaseLoi />} />
        <Route path="funding" element={<FundingReadiness />} />
        <Route path="alerts" element={<Alerts />} />
        <Route path="pros" element={<Pros />} />
        <Route path="company" element={<Company />} />
        <Route path="documents" element={<Documents />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="awards" element={<Awards />} />
        <Route path="awards/:id" element={<AwardDetail />} />
        <Route path="inquiries" element={<Inquiries />} />
      </Route>
      <Route path="/invite/:token" element={<Invite />} />
      <Route path="/verify/:id" element={<Verify />} />
      <Route path="/verified" element={<BadgeWall />} />
      <Route path="/terms" element={<Legal />} />
      <Route path="/privacy" element={<Legal />} />
      <Route path="/contractor-agreement" element={<Legal />} />
      <Route path="/partners" element={<Partners />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/about" element={<About />} />
      <Route path="/features" element={<Features />} />
      <Route path="/compare/:slug" element={<Compare />} />
      <Route path="/founder-cohort" element={<FounderCohort />} />
      <Route path="/cohort-checkin/:token" element={<CohortCheckin />} />
      <Route path="/case-study-approve/:token" element={<CaseStudyApprove />} />
      <Route path="/customers" element={<Customers />} />
      <Route path="/top-grant-industries" element={<TopGrantIndustries />} />
      <Route path="/grants-for/:slug" element={<IndustryPage />} />
      <Route path="/grants-for/:slug/:state" element={<StateIndustryPage />} />
      <Route path="/funding-ready/:shareId" element={<FundingReadyPublic />} />
      <Route path="/workshop/:id" element={<WorkshopRegister />} />
      <Route path="/workshop/:id/cancel" element={<WorkshopCancel />} />
      <Route path="/workshop/:id/feedback" element={<WorkshopFeedback />} />
      <Route path="/library/:slug" element={<LibraryPage />} />
      <Route path="/library-stats/:token" element={<LibraryStats />} />
      <Route path="/verified/alerts/:token" element={<SavedSearchAction />} />
      <Route path="/pro" element={<ProtectedRoute pro><ProPortal /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <TourProvider>
          <AppRouter />
          <CookieBanner />
        </TourProvider>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
